import { IconEdit } from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { colors } from 'constants/colors';
import { DynamicDeliveryTag } from 'components/DynamicDeliveryTag';
import { SETUP_STATE_COLORS } from '../constants/states';

import {
    GetSiteSetupsForDateQuery,
    MicrostoreColumnGroupSetupFragmentFragment,
    MicrostoreColumnGroupSetupState,
    MicrostoreColumnSetupFragmentFragment,
    MicrostoreColumnSetupState,
    SetupInstructionFragmentFragment,
    SiteSetupFragmentFragment,
    SiteSetupState,
} from 'data/__generated__';

import { SETUP_INSTRUCTION_TYPE_LABELS } from '../constants/types';
import { formatDateForDisplay } from 'helpers/dateTimes';

function computeDurationFromStateHistory({
    isNeeded,
    isFinishForced,
    stateHistory,
}: {
    isNeeded: boolean;
    isFinishForced: boolean;
    stateHistory:
        | SiteSetupFragmentFragment['stateHistory']
        | MicrostoreColumnGroupSetupFragmentFragment['stateHistory']
        | MicrostoreColumnSetupFragmentFragment['stateHistory']
        | SetupInstructionFragmentFragment['stateHistory'];
}): string {
    if (!isNeeded) {
        return 'Pas a faire';
    }

    const startTime = stateHistory.find(({ state }) => state === SiteSetupState.InProgress)?.createdAt;
    const endTime = stateHistory.find(({ state }) => state === SiteSetupState.Finished)?.createdAt;

    if (!startTime && endTime) {
        return 'Fait à la main';
    }
    if (!startTime) {
        return 'Pas commencé';
    }
    if (!endTime) {
        return 'Pas finis';
    }

    const dateDiff = endTime - startTime;
    return `${Math.round(dateDiff / 1000 / 60)}\u00A0min\u00A0${
        isFinishForced ? '(Finition Forcée)' : ''
    }\u00A0${new Date(startTime).toLocaleTimeString()}\u00A0-\u00A0${new Date(endTime).toLocaleTimeString()}`;
}

export const SiteSetupTrelloItem = ({
    isUsedInTrelloListView = true,
    siteSetup,
    showDate = false,
}: {
    isUsedInTrelloListView?: boolean;
    siteSetup: GetSiteSetupsForDateQuery['getSiteSetupsForDate'][number] | SiteSetupFragmentFragment;
    showDate?: boolean;
}) => {
    return (
        <ItemContent>
            <ItemHeader $backgroundColor={siteSetup.isFinishForced ? colors.red : SETUP_STATE_COLORS[siteSetup.state]}>
                <HeaderLeft>
                    <span>
                        {siteSetup.site.name} (
                        {computeDurationFromStateHistory({
                            isFinishForced: siteSetup.isFinishForced,
                            isNeeded: true,
                            stateHistory: siteSetup.stateHistory,
                        })}
                        )
                    </span>
                    <DynamicDeliveryTag isDynamic={siteSetup.delivery?.isDynamic} label="Rangement dynamique" />
                </HeaderLeft>
                {isUsedInTrelloListView ? (
                    <Link to={`/site-setup/${siteSetup._id}`}>
                        <EditIcon color={colors.pureWhite} size="15" />
                    </Link>
                ) : null}
            </ItemHeader>
            <ItemBody>
                {showDate ? <DateText>Rangement du {formatDateForDisplay(siteSetup.date)}</DateText> : null}
                {siteSetup.microstoreColumnGroupSetups.map((microstoreColumnGroupSetup) => {
                    const microstoreColumnGroup = siteSetup.site.microstoreColumnGroups.find(
                        ({ _id }) => _id === microstoreColumnGroupSetup.microstoreColumnGroupId,
                    );
                    if (!microstoreColumnGroup) {
                        return null;
                    }

                    return (
                        <MicrostoreColumnGroupDetails
                            key={microstoreColumnGroupSetup._id}
                            open={microstoreColumnGroupSetup.state === MicrostoreColumnGroupSetupState.InProgress}
                        >
                            <MicrostoreColumnGroupSummary
                                $color={
                                    microstoreColumnGroupSetup.isFinishForced
                                        ? colors.red
                                        : SETUP_STATE_COLORS[microstoreColumnGroupSetup.state]
                                }
                            >
                                {microstoreColumnGroup.name} (
                                {computeDurationFromStateHistory({
                                    isFinishForced: microstoreColumnGroupSetup.isFinishForced,
                                    isNeeded: microstoreColumnGroupSetup.isNeeded,
                                    stateHistory: microstoreColumnGroupSetup.stateHistory,
                                })}
                                )
                            </MicrostoreColumnGroupSummary>
                            {microstoreColumnGroupSetup.microstoreColumnSetups.map((microstoreColumnSetup) => {
                                const microstoreColumn = microstoreColumnGroup.columns.find(
                                    ({ _id }) => _id === microstoreColumnSetup.microstoreColumnId,
                                );
                                if (!microstoreColumn) {
                                    return null;
                                }

                                return (
                                    <MicrostoreColumnDetails
                                        key={microstoreColumnSetup._id}
                                        open={microstoreColumnSetup.state === MicrostoreColumnSetupState.InProgress}
                                    >
                                        <MicrostoreColumnSummary
                                            $color={
                                                microstoreColumnSetup.isFinishForced
                                                    ? colors.red
                                                    : SETUP_STATE_COLORS[microstoreColumnSetup.state]
                                            }
                                        >
                                            {microstoreColumn.name} (
                                            {computeDurationFromStateHistory({
                                                isFinishForced: microstoreColumnSetup.isFinishForced,
                                                isNeeded: microstoreColumnSetup.isNeeded,
                                                stateHistory: microstoreColumnSetup.stateHistory,
                                            })}
                                            )
                                        </MicrostoreColumnSummary>
                                        {microstoreColumnSetup.instructions.map((instruction, index) => {
                                            return (
                                                <Instruction
                                                    key={index}
                                                    $color={
                                                        instruction.isFinishForced
                                                            ? colors.red
                                                            : SETUP_STATE_COLORS[instruction.state]
                                                    }
                                                >
                                                    - {SETUP_INSTRUCTION_TYPE_LABELS[instruction.type]} (
                                                    {computeDurationFromStateHistory({
                                                        isFinishForced: instruction.isFinishForced,
                                                        isNeeded: instruction.isNeeded,
                                                        stateHistory: instruction.stateHistory,
                                                    })}
                                                    )
                                                </Instruction>
                                            );
                                        })}
                                    </MicrostoreColumnDetails>
                                );
                            })}
                        </MicrostoreColumnGroupDetails>
                    );
                })}
            </ItemBody>
        </ItemContent>
    );
};

const ItemContent = styled.div`
    display: flex;
    flex-direction: column;
    border-radius: ${({ theme }) => theme.borderRadius};
    border: 1px solid ${({ theme }) => theme.lightBorderColor};
    background-color: ${({ theme }) => theme.backgroundColor};
    color: ${({ theme }) => theme.textColor};
    overflow: hidden;
`;

const ItemHeader = styled.div<{ $backgroundColor: string }>`
    display: flex;
    align-items: center;
    justify-content: space-between;
    background-color: ${({ $backgroundColor }) => $backgroundColor};
    color: ${colors.pureWhite};
    padding: 10px;
    font-weight: 800;
    text-align: center;
`;

const HeaderLeft = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
`;

const ItemBody = styled.div`
    padding: 10px;
`;

const EditIcon = styled(IconEdit)`
    flex-shrink: 0;
    cursor: pointer;

    &:hover {
        transform: scale(1.1);
    }
`;

const MicrostoreColumnGroupDetails = styled.details`
    &:not(:first-child) {
        margin-top: 10px;
    }
`;

const MicrostoreColumnGroupSummary = styled.summary<{ $color: string }>`
    font-weight: 700;
    color: ${({ $color }) => $color};
    cursor: pointer;
`;

const MicrostoreColumnDetails = styled.details`
    margin-top: 5px;
`;

const MicrostoreColumnSummary = styled.summary<{ $color: string }>`
    padding-left: 20px;
    color: ${({ $color }) => $color};
    cursor: pointer;
`;

const Instruction = styled.div<{ $color?: string }>`
    margin-left: 40px;
    color: ${({ $color }) => $color ?? colors.pureBlack};

    &:nth-child(2) {
        margin-top: 5px;
    }
    &:nth-child(n + 3) {
        margin-top: 10px;
    }
`;

const DateText = styled.span`
    color: ${colors.pantinGrey};
    font-weight: 700;
`;
